import { Send } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  InputArea,
  MessageBubble,
  MessageMarkdown,
  MessagesArea,
  MessageWrapper,
  StyledTextField,
} from '@/console/features/factory-operator/components/chat/chat.styled';
import { TChatProps } from '@/console/features/factory-operator/components/chat/chat.types';
import { ChoiceSelector } from '@/console/features/factory-operator/components/choice-selector/choice-selector';
import {
  FACTORY_OPERATOR_THINKING_MESSAGE,
  FACTORY_OPERATOR_USER_ID,
  NEW_CHAT,
} from '@/console/features/factory-operator/factory-operator.constants';
import { useChat } from '@/console/features/factory-operator/hooks/use-chat/use-chat';
import { AiLoader } from '@/shared/components/ui/ai-loader/ai-loader';
import { IconBgButton } from '@/shared/components/ui/button/icon-bg-button/icon-bg-button';
import { MarkdownLink } from '@/shared/components/ui/markdown-link/markdown-link';
import {
  sdaiApi,
  useContinueChatMutation,
  useReadChatQuery,
  useStartChatMutation,
} from '@/shared/redux/api/sdai/sdai-api';
import { ChoiceType } from '@/shared/types/types.generated';
import { showToastError } from '@/shared/utils/toast';

type TChatMessage = {
  userId: string;
  message: string;
  creationTimestamp?: any;
  choices?: ChoiceType[];
};

export const Chat: React.FC<TChatProps> = ({ chatId, setCurrentChatId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { data: chatData, isLoading: isReadChatLoading } = useReadChatQuery(
    { chatId },
    { skip: chatId === NEW_CHAT },
  );
  const {
    operatorThinking,
    setOperatorThinking,
    message: inputMessage,
    setMessage: setInputMessage,
  } = useChat(chatId);
  const [startChat, { isLoading: isStartChatLoading }] = useStartChatMutation();
  const [continueChat, { isLoading: isContinueChatLoading }] =
    useContinueChatMutation();

  const isChatLoading =
    isStartChatLoading ||
    isReadChatLoading ||
    isContinueChatLoading ||
    operatorThinking;

  const initialMessage: TChatMessage = {
    userId: FACTORY_OPERATOR_USER_ID,
    message: t('factory_operator:start_chat_message'),
    creationTimestamp: new Date(),
  };

  const getChatMessages = (): TChatMessage[] => {
    const baseMessages: TChatMessage[] =
      chatId === NEW_CHAT || !chatData?.readChat?.history
        ? [initialMessage]
        : [...chatData.readChat.history, initialMessage];

    if (operatorThinking) {
      return [
        {
          userId: FACTORY_OPERATOR_USER_ID,
          message: FACTORY_OPERATOR_THINKING_MESSAGE,
          creationTimestamp: new Date().toISOString(),
        },
        ...baseMessages,
      ];
    }

    return baseMessages;
  };

  const chatMessages = getChatMessages();

  const isUserMessage = (userId: string) => userId !== FACTORY_OPERATOR_USER_ID;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderMessageContent = (messageContent: string, isUser: boolean) => {
    if (messageContent === 'start_chat_message') {
      messageContent = t('factory_operator:start_chat_message');
    }

    if (messageContent === FACTORY_OPERATOR_THINKING_MESSAGE) {
      return <AiLoader showIcon={false} />;
    }

    const formattedContent = messageContent.replace(
      '__BASE_URL__',
      window.location.origin,
    );

    return (
      <MessageMarkdown
        $isUser={isUser}
        source={formattedContent}
        components={{
          a: MarkdownLink,
        }}
      />
    );
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText ?? inputMessage;
    try {
      let chatResponse;
      if (!chatId || chatId === NEW_CHAT) {
        chatResponse = await handleStartChat(textToSend);
      } else {
        chatResponse = await handleContinueChat(textToSend);
      }

      dispatch(
        sdaiApi.util.upsertQueryData(
          'ReadChat',
          { chatId: chatResponse.chatId },
          { readChat: chatResponse },
        ) as any,
      );

      setOperatorThinking(true);
      setCurrentChatId(chatResponse.chatId);
      setInputMessage('');
    } catch {
      showToastError(t('factory_operator:send_message_error'));
    }
  };

  const handleStartChat = async (textToSend: string) => {
    const response = await startChat({
      message: {
        message: textToSend,
      },
    }).unwrap();

    return response.startChat;
  };

  const handleContinueChat = async (textToSend: string) => {
    const response = await continueChat({
      message: {
        message: textToSend,
        chatId,
      },
    }).unwrap();

    return response.continueChat;
  };

  const lastOperatorMessage = chatMessages[0];
  const activeChoices =
    lastOperatorMessage &&
    !isUserMessage(lastOperatorMessage.userId) &&
    !isChatLoading &&
    lastOperatorMessage.choices?.length
      ? lastOperatorMessage.choices
      : null;

  const handleChoicesComplete = (formattedMessage: string) => {
    void handleSendMessage(formattedMessage);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, chatId]);

  return (
    <Stack justifyContent="space-between" height="100%">
      <MessagesArea>
        {chatMessages
          ?.slice()
          .reverse()
          .map((chatMessage, index) => {
            const isUser = isUserMessage(chatMessage.userId);
            return (
              <MessageWrapper key={index} $isUser={isUser}>
                <MessageBubble $isUser={isUser}>
                  {renderMessageContent(chatMessage.message, isUser)}
                  <Typography
                    variant="p10"
                    color="textSecondary"
                    alignSelf="flex-end"
                  >
                    {formatTimestamp(chatMessage.creationTimestamp)}
                  </Typography>
                </MessageBubble>
              </MessageWrapper>
            );
          })}
        <div ref={messagesEndRef} />
      </MessagesArea>
      {activeChoices && (
        <ChoiceSelector
          choices={activeChoices}
          onComplete={handleChoicesComplete}
        />
      )}
      <InputArea>
        <StyledTextField
          fullWidth
          placeholder={t('factory_operator:type_a_message')}
          variant="outlined"
          size="small"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          multiline
          disabled={isChatLoading}
          maxRows={3}
        />
        <IconBgButton
          variant="secondary"
          onClick={() => handleSendMessage()}
          disabled={!inputMessage.trim() || isChatLoading}
        >
          <Send />
        </IconBgButton>
      </InputArea>
    </Stack>
  );
};
